import clsx from 'clsx';
import {
    type ComponentPropsWithoutRef,
    type CSSProperties,
    type FunctionComponent,
    type RefCallback,
    type RefObject,
    useCallback,
    useLayoutEffect,
    useState,
} from 'react';

export type ScrollFadeOrientation = 'horizontal' | 'vertical';

export const HIDDEN_SCROLLBAR_CLASS_NAME = '[scrollbar-width:none] [&::-webkit-scrollbar]:hidden';

export type ScrollFadeProps = ComponentPropsWithoutRef<'div'> & {
    /** How much shows at an overflowing edge, 0 none to 1 all; by default half. */
    readonly edgeFadeOpacity?: number;
    readonly edgeFadeSize?: string;
    readonly orientation: ScrollFadeOrientation;
    readonly viewportRef?: RefObject<HTMLDivElement | null>;
};

type OverflowEdges = {
    readonly end: boolean;
    readonly start: boolean;
};

type UseScrollFadeOptions = {
    readonly disabled?: boolean;
    readonly edgeFadeOpacity?: number;
    readonly edgeFadeSize?: string;
    readonly orientation: ScrollFadeOrientation;
    readonly viewportRef?: RefObject<HTMLDivElement | null>;
};

const DEFAULT_EDGE_FADE_SIZE = 'var(--spacing-xxl)';
// Masks only read the alpha channel; 50% alpha halves the fade strength at the edge.
const DEFAULT_EDGE_FADE_OPACITY = 0.5;
const SCROLL_FADE_HEADER_ATTRIBUTE = 'data-scroll-fade-header';
const SCROLL_FADE_HEADER_SELECTOR = `[${SCROLL_FADE_HEADER_ATTRIBUTE}]`;

export const SCROLL_FADE_HEADER_PROPS = {
    [SCROLL_FADE_HEADER_ATTRIBUTE]: '',
} as const;

const getEdgeFadeMask = ({
    edgeFadeOpacity,
    edgeFadeSize,
    end,
    orientation,
    start,
    startOffset,
}: OverflowEdges & {
    readonly edgeFadeOpacity: number;
    readonly edgeFadeSize: string;
    readonly orientation: ScrollFadeOrientation;
    readonly startOffset: number;
}): string | undefined => {
    const direction = orientation === 'horizontal' ? 'right' : 'bottom';
    const edgeColor = `rgb(0 0 0 / ${edgeFadeOpacity})`;
    const startFade =
        startOffset > 0
            ? `currentColor 0, currentColor ${startOffset}px, ${edgeColor} ${startOffset}px, currentColor calc(${startOffset}px + ${edgeFadeSize})`
            : `${edgeColor} 0, currentColor ${edgeFadeSize}`;

    if (start && end) {
        return `linear-gradient(to ${direction}, ${startFade}, currentColor calc(100% - ${edgeFadeSize}), ${edgeColor} 100%)`;
    }

    if (start) {
        return `linear-gradient(to ${direction}, ${startFade}, currentColor 100%)`;
    }

    if (end) {
        return `linear-gradient(to ${direction}, currentColor 0, currentColor calc(100% - ${edgeFadeSize}), ${edgeColor} 100%)`;
    }

    return undefined;
};

export const useScrollFade = ({
    disabled = false,
    edgeFadeOpacity = DEFAULT_EDGE_FADE_OPACITY,
    edgeFadeSize = DEFAULT_EDGE_FADE_SIZE,
    orientation,
    viewportRef,
}: UseScrollFadeOptions) => {
    const [viewport, setViewport] = useState<HTMLDivElement | null>(null);
    const [overflowEdges, setOverflowEdges] = useState<OverflowEdges>({ end: false, start: false });
    const [startOffset, setStartOffset] = useState(0);
    const setViewportRef = useCallback<RefCallback<HTMLDivElement>>(
        (node) => {
            if (viewportRef !== undefined) viewportRef.current = node;
            setViewport(node);
        },
        [viewportRef],
    );
    const updateOverflowEdges = useCallback((): void => {
        if (viewport === null || disabled) return;

        const scrollOffset = orientation === 'horizontal' ? viewport.scrollLeft : viewport.scrollTop;
        const scrollSize = orientation === 'horizontal' ? viewport.scrollWidth : viewport.scrollHeight;
        const viewportSize = orientation === 'horizontal' ? viewport.clientWidth : viewport.clientHeight;
        const maxScrollOffset = Math.max(0, scrollSize - viewportSize);
        const nextEdges = {
            end: scrollOffset < maxScrollOffset - 1,
            start: scrollOffset > 1,
        };

        setOverflowEdges((currentEdges) =>
            currentEdges.start === nextEdges.start && currentEdges.end === nextEdges.end ? currentEdges : nextEdges,
        );
    }, [disabled, orientation, viewport]);
    const updateStartOffset = useCallback((): void => {
        if (viewport === null || disabled) return;
        if (orientation !== 'vertical') {
            setStartOffset(0);
            return;
        }

        const fadeHeader = viewport.querySelector<HTMLElement>(SCROLL_FADE_HEADER_SELECTOR);
        const nextStartOffset =
            fadeHeader === null
                ? 0
                : Math.max(
                      0,
                      Math.min(
                          viewport.clientHeight,
                          Math.round(fadeHeader.getBoundingClientRect().bottom - viewport.getBoundingClientRect().top),
                      ),
                  );

        setStartOffset((currentStartOffset) =>
            currentStartOffset === nextStartOffset ? currentStartOffset : nextStartOffset,
        );
    }, [disabled, orientation, viewport]);

    useLayoutEffect(() => {
        if (viewport === null || disabled) return;

        updateOverflowEdges();
        updateStartOffset();
        viewport.addEventListener('scroll', updateOverflowEdges, { passive: true });

        if (typeof ResizeObserver === 'undefined') {
            return () => viewport.removeEventListener('scroll', updateOverflowEdges);
        }

        const resizeObserver = new ResizeObserver(() => {
            updateOverflowEdges();
            updateStartOffset();
        });
        resizeObserver.observe(viewport);
        const observedElements = new Set<Element>();
        const syncObservedElements = (): void => {
            const nextObservedElements = new Set<Element>(viewport.children);
            const fadeHeader = viewport.querySelector<HTMLElement>(SCROLL_FADE_HEADER_SELECTOR);

            if (fadeHeader !== null) nextObservedElements.add(fadeHeader);

            for (const element of observedElements) {
                if (!nextObservedElements.has(element)) resizeObserver.unobserve(element);
            }

            for (const element of nextObservedElements) {
                if (!observedElements.has(element)) resizeObserver.observe(element);
            }

            observedElements.clear();
            for (const element of nextObservedElements) observedElements.add(element);
        };
        const mutationObserver = new MutationObserver(() => {
            syncObservedElements();
            updateOverflowEdges();
            updateStartOffset();
        });
        syncObservedElements();
        mutationObserver.observe(viewport, {
            attributeFilter: [SCROLL_FADE_HEADER_ATTRIBUTE],
            attributes: true,
            childList: true,
            subtree: true,
        });

        return () => {
            viewport.removeEventListener('scroll', updateOverflowEdges);
            mutationObserver.disconnect();
            resizeObserver.disconnect();
        };
    }, [disabled, updateOverflowEdges, updateStartOffset, viewport]);

    const edgeFadeMask = disabled
        ? undefined
        : getEdgeFadeMask({ ...overflowEdges, edgeFadeOpacity, edgeFadeSize, orientation, startOffset });

    return {
        overflowEdges,
        style: {
            maskImage: edgeFadeMask,
            WebkitMaskImage: edgeFadeMask,
        } satisfies CSSProperties,
        viewportRef: setViewportRef,
    };
};

export const ScrollFade: FunctionComponent<ScrollFadeProps> = ({
    children,
    className,
    edgeFadeOpacity,
    edgeFadeSize,
    onScroll,
    orientation,
    style,
    viewportRef,
    ...props
}) => {
    const scrollFade = useScrollFade({ edgeFadeOpacity, edgeFadeSize, orientation, viewportRef });
    const edgeFadeStyle: CSSProperties = { ...style, ...scrollFade.style };

    return (
        <div
            {...props}
            className={clsx(
                HIDDEN_SCROLLBAR_CLASS_NAME,
                orientation === 'horizontal' ? 'min-w-0 overflow-x-auto' : 'min-h-0 overflow-y-auto',
                className,
            )}
            data-overflow-bottom={orientation === 'vertical' ? scrollFade.overflowEdges.end : undefined}
            data-overflow-left={orientation === 'horizontal' ? scrollFade.overflowEdges.start : undefined}
            data-overflow-right={orientation === 'horizontal' ? scrollFade.overflowEdges.end : undefined}
            data-overflow-top={orientation === 'vertical' ? scrollFade.overflowEdges.start : undefined}
            ref={scrollFade.viewportRef}
            style={edgeFadeStyle}
            onScroll={onScroll}
        >
            {children}
        </div>
    );
};
